import { MILITARY_FORMAT } from '@helpers/datetime';
import { getEstimatedFee } from '@helpers/getEstimatedFee';
import {
  eventDuration,
  isAmericanExpress,
  serviceFee,
  shiftDuration,
  shiftRate,
  totalEventRate,
} from '@helpers/post';
import { rateTypeNameFinder } from '@helpers/rateTypeName';
import { get, groupBy } from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import { Modal } from 'reactstrap';
import { Button, Card } from '@dev-go4ellis/go4-design-system';

export const EstimatedCost = ({ shifts, shiftLocation, rateTypes, payment, currentUser }) => {
  const [showBreakdown, setShowBreakdown] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const payRate = get(shiftLocation, 'payRate', 0);
  const rateTypeId = get(shiftLocation, 'rateTypeId', 0);
  const maxStartTime = Math.max(...shifts.map(({ startTime }) => moment(startTime, MILITARY_FORMAT)));

  const totalCapacity = shifts.reduce((acc, shift) => acc + shift.capacity, 0);
  const totalDuration = eventDuration({ shifts });
  const totalRate = totalEventRate({ shifts });

  const fee = (currentUser.effectivePaymentFee !== null && currentUser.effectivePaymentFee !== undefined)
    ? currentUser.effectivePaymentFee * 100
    : getEstimatedFee(maxStartTime, 0, isAmericanExpress(payment));
  const locationServiceFee = serviceFee({ shifts, payment, currentUser });
  const estimatedTotal = locationServiceFee + totalRate;

  const displayRate = () => {
    const type = rateTypeNameFinder(rateTypes, rateTypeId);
    return `${payRate}/${type}`;
  };

  const setPrecision = amount => Number(amount).toFixed(2);
  const isHourly = () => Number(rateTypeId) === 0;

  const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

  const shiftGroups = Object.entries(groupBy(shifts, shiftDuration)).map(([duration, group]) => ({
    duration,
    capacity: group.reduce((acc, shift) => acc + shift.capacity, 0),
    rate: group.reduce((acc, shift) => acc + shiftRate(shift), 0),
  }));

  const estimatedTotalCopy = () => {
    const items = [pluralize(totalCapacity, 'AT', 'ATs'), pluralize(shifts.length, 'shift', 'shifts')];
    if (isHourly()) items.push(pluralize(totalDuration, 'hour', 'hours'));

    const schedule = items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items.at(-1)}` : items[0];

    return `Estimate uses the ${schedule} in your schedule. You are billed for hours actually worked.`;
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const serviceFeeCoverage = [
    {
      icon: 'verified_user',
      title: 'Licensure verification',
      body: 'Every trainer who accepts your job is credential-checked before they arrive on site.',
    },
    {
      icon: 'shield',
      title: 'Insurance',
      body: 'Trainers working through Go4 are covered while they are on your event.',
    },
    {
      icon: 'payments',
      title: 'Payments and payroll',
      body: 'Go4 handles trainer payment and tax paperwork. One invoice, charged once after the shift.',
    },
    {
      icon: 'description',
      title: 'Documentation',
      body: 'HIPAA-compliant injury reports, treatment logs and Emergency Action Plans stored in your account.',
    },
  ];

  const serviceFeePoints = [
    'Free to post. You are only charged after a trainer works the shift.',
    'Go4 never sets or marks up trainer pay. Every dollar of the rate you set goes to the trainer.',
    'No contracts, subscriptions or minimums.',
  ];

  return (
    <div className="estimated-costs">
      <div className="estimated-total-container">
        <p className="estimated-total-header">
          Estimated total for this event{' '}
          <span className="gotham-bold-font">${setPrecision(estimatedTotal)}</span>
        </p>
        <span className="breakdown-toggle" onClick={() => setShowBreakdown(!showBreakdown)}>
          {showBreakdown ? 'Hide breakdown' : 'Show breakdown'}
          <i className={`fas ${showBreakdown ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
        </span>
      </div>

      {showBreakdown && (
        <div className="estimated-summary-container">
          {isHourly() ? (
            shiftGroups.map(group => (
              <div className="line" key={group.duration}>
                <p>
                  {group.capacity}
                  {group.capacity === 1 ? 'AT' : 'ATs'} x {group.duration}hrs x {displayRate()}
                </p>
                <span>${setPrecision(group.rate)}</span>
              </div>
            ))
          ) : (
            <div className="line">
              <p>
                {totalCapacity}
                {totalCapacity === 1 ? 'AT' : 'ATs'} x {displayRate()}
              </p>
              <span>${setPrecision(totalRate)}</span>
            </div>
          )}
          <div className="line">
            <p>Go4 service fee ({fee}%)</p>
            <span>${setPrecision(locationServiceFee)}</span>
          </div>

          <span className="service-fee-info" onClick={() => toggle()}>
            What's this?
          </span>

          <div className="horizontal-line" />

          <div className="line gotham-bold-font">
            <p>Estimated total</p>
            <span>${setPrecision(estimatedTotal)}</span>
          </div>

          <p className="estimated-notice">{estimatedTotalCopy()}</p>
        </div>
      )}

      <Modal isOpen={isOpen} toggle={toggle} centered className="service-fee-modal-content">
        <div className="service-fee-modal">
          <div className="service-fee-modal__header">
            <div className="service-fee-modal__heading">
              <div className="service-fee-modal__eyebrow">One flat {fee}% service fee</div>
              <h1 className="service-fee-modal__title">What the Go4 service fee covers</h1>
            </div>
            <span className="material-icons service-fee-modal__close" onClick={toggle}>
              close
            </span>
          </div>

          <div className="service-fee-modal__body">
            <p className="service-fee-modal__intro">
              You set the hourly rate. Go4 adds one {fee}% service fee that covers licensure verification,
              payments, insurance and documentation. No subscriptions, no per-seat pricing.
            </p>

            <div className="service-fee-modal__grid">
              {serviceFeeCoverage.map(({ icon, title, body }) => (
                <Card className="service-fee-card" key={title}>
                  <div className="service-fee-card__head">
                    <span className="material-icons service-fee-card__icon">{icon}</span>
                    <span className="service-fee-card__title">{title}</span>
                  </div>
                  <div className="service-fee-card__body">{body}</div>
                </Card>
              ))}
            </div>

            <div className="service-fee-modal__points">
              {serviceFeePoints.map(point => (
                <div className="service-fee-modal__point" key={point}>
                  <span className="material-icons service-fee-modal__point-icon">check_circle</span>
                  <span className="service-fee-modal__point-text">{point}</span>
                </div>
              ))}
            </div>

            <div className="service-fee-modal__footer">
              <p className="service-fee-modal__note">
                Service fee may be adjusted to 21% if payment is provided via American Express
              </p>
              <Button className="service-fee-modal__cta" onClick={toggle}>
                GOT IT
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
